const QUOTE_ERROR = {
  offer_not_found: "ההצעה לא נמצאה בשרת.",
  offer_expired: "תוקף ההצעה פג.",
  offer_mismatch: "פרטי ההצעה לא תואמים את המחיר שאומת.",
  quote_unavailable: "לא ניתן לאמת את המחיר כרגע.",
  quote_missing: "חסר אימות מחיר מהשרת.",
  unauthorized: "נדרשת התחברות מחדש.",
  price_changed: "המחיר השתנה. יש לאשר את המחיר המעודכן.",
};

function hasRoomTerms(normalized) {
  const display = normalized?.display;
  if (!display) return false;
  const cancellation = display.cancellation;
  const frames = cancellation?.frames;
  if (cancellation?.type) return true;
  if (Array.isArray(frames) && frames.length > 0) return true;
  if (display.boardSummary) return true;
  if ((display.remarks || []).some((row) => row?.text)) return true;
  return false;
}

function availabilityStep({ prebookLoading, prebookError, normalized }) {
  if (prebookLoading || (!normalized && !prebookError)) {
    return {
      id: "availability",
      label: "בודקים זמינות",
      status: "active",
      detail: "ממתין לתשובת הספק",
    };
  }
  if (prebookError) {
    return {
      id: "availability",
      label: "בודקים זמינות",
      status: "error",
      detail: String(prebookError),
    };
  }
  if (normalized?.supplierConfirmed) {
    return {
      id: "availability",
      label: "בודקים זמינות",
      status: "done",
      detail: "החדר זמין",
    };
  }
  const missing = (normalized?.criticalMissing || []).filter(Boolean).join(", ");
  return {
    id: "availability",
    label: "בודקים זמינות",
    status: "error",
    detail: missing
      ? `הספק לא אישר את החדר (${missing})`
      : "הספק לא אישר את זמינות החדר",
  };
}

function priceStep({ quoteLoading, quote, quoteEnabled }) {
  if (quoteLoading || (quoteEnabled && !quote)) {
    return {
      id: "price",
      label: "מאמתים מחיר",
      status: "active",
      detail: "ממתין לתשובת המחיר מהשרת",
    };
  }
  if (quote?.status === "price_changed") {
    return {
      id: "price",
      label: "מאמתים מחיר",
      status: "error",
      detail: QUOTE_ERROR.price_changed,
    };
  }
  if (quote?.source === "server_quote" && quote?.binding === true) {
    return {
      id: "price",
      label: "מאמתים מחיר",
      status: "done",
      detail: "המחיר אומת",
    };
  }
  const known = QUOTE_ERROR[quote?.status];
  return {
    id: "price",
    label: "מאמתים מחיר",
    status: "error",
    detail: known || "המחיר לא אומת",
  };
}

function termsStep({ prebookLoading, prebookError, normalized }) {
  if (prebookLoading || (!normalized && !prebookError)) {
    return {
      id: "terms",
      label: "מאשרים תנאי חדר",
      status: "pending",
      detail: "ממתין לתשובת הספק",
    };
  }
  if (prebookError || !normalized?.supplierConfirmed) {
    return {
      id: "terms",
      label: "מאשרים תנאי חדר",
      status: "error",
      detail: "לא ניתן לאשר את תנאי החדר בלי אישור זמינות",
    };
  }
  if (hasRoomTerms(normalized)) {
    return {
      id: "terms",
      label: "מאשרים תנאי חדר",
      status: "done",
      detail: "תנאי החדר התקבלו מהספק",
    };
  }
  return {
    id: "terms",
    label: "מאשרים תנאי חדר",
    status: "error",
    detail: "הספק לא החזיר את תנאי החדר",
  };
}

/**
 * Room confirmation steps from the prebook response and the server quote.
 * A step stays pending or active until that response arrives. Nothing advances on a timer.
 */
export function roomConfirmState({
  prebookLoading = false,
  prebookError = null,
  normalized = null,
  quoteLoading = false,
  quote = null,
  quoteEnabled = false,
} = {}) {
  const availability = availabilityStep({ prebookLoading, prebookError, normalized });
  const price = priceStep({ quoteLoading, quote, quoteEnabled });
  const terms = termsStep({ prebookLoading, prebookError, normalized });
  const checks = [availability, price, terms];
  const ready = checks.every((step) => step.status === "done");
  const failed = checks.some((step) => step.status === "error");
  const approval = ready
    ? { id: "approved", label: "החדר אושר", status: "done", detail: "החדר אושר" }
    : failed
      ? { id: "approved", label: "החדר אושר", status: "error", detail: "החדר לא אושר" }
      : { id: "approved", label: "החדר אושר", status: "pending", detail: "ממתין לסיום האימות" };
  const failedStep = checks.find((step) => step.status === "error") || null;

  return {
    steps: [...checks, approval],
    approved: ready,
    failure: failedStep
      ? {
          stepId: failedStep.id,
          message: failedStep.detail,
          canAcknowledge: failedStep.id === "price" && quote?.status === "price_changed",
          canRetry: failedStep.id !== "price",
        }
      : null,
  };
}
